import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import EditIcon from '@/letsgo/icons/Edit';
import DeleteIcon from '@/letsgo/icons/Delete';

interface Permission {
  id: string;
  name: string;
  description?: string;
}

interface CustomRoleDetail {
  id: string;
  name: string;
  description?: string;
  permissions: { permission: Permission }[];
}

interface CustomRoleManagementProps {
  teamId: string;
}

const CustomRoleManagement: React.FC<CustomRoleManagementProps> = ({ teamId }) => {
  const [customRoles, setCustomRoles] = useState<CustomRoleDetail[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<CustomRoleDetail | null>(null);
  const [roleName, setRoleName] = useState<string>('');
  const [roleDescription, setRoleDescription] = useState<string>('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const fetchRolesAndPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        fetch(`/api/teams/${teamId}/custom-roles`),
        fetch('/api/permissions'), // Assuming an API route to fetch all available permissions
      ]);

      if (!rolesResponse.ok) {
        const errorData = await rolesResponse.json();
        throw new Error(errorData.message || `Failed to fetch custom roles: ${rolesResponse.status}`);
      }
      if (!permissionsResponse.ok) {
        const errorData = await permissionsResponse.json();
        throw new Error(errorData.message || `Failed to fetch permissions: ${permissionsResponse.status}`);
      }

      const rolesData = await rolesResponse.json();
      const permissionsData = await permissionsResponse.json();

      setCustomRoles(rolesData);
      setPermissions(permissionsData);
    } catch (err: any) {
      console.error("Error fetching custom roles or permissions:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (teamId) {
      fetchRolesAndPermissions();
    }
  }, [teamId]);

  const handleOpenCreateModal = () => {
    setEditingRole(null);
    setRoleName('');
    setRoleDescription('');
    setSelectedPermissions([]);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (role: CustomRoleDetail) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    setSelectedPermissions(role.permissions.map(p => p.permission.name));
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRole(null);
  };

  const handlePermissionChange = (permissionName: string) => {
    setSelectedPermissions((prevSelected) =>
      prevSelected.includes(permissionName)
        ? prevSelected.filter((name) => name !== permissionName)
        : [...prevSelected, permissionName]
    );
  };

  const handleSaveRole = async () => {
    if (!roleName) {
      setError('Role name cannot be empty.');
      return;
    }

    try {
      const method = editingRole ? 'PUT' : 'POST';
      const url = editingRole ? `/api/teams/${teamId}/custom-roles/${editingRole.id}` : `/api/teams/${teamId}/custom-roles`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: roleName,
          description: roleDescription,
          permissionNames: selectedPermissions,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to save custom role: ${response.status}`);
      }

      console.log(`Custom role ${editingRole ? 'updated' : 'created'} successfully!`);
      fetchRolesAndPermissions(); // Refetch data
      handleCloseModal();
      // TODO: Add success notification
    } catch (err: any) {
      console.error('Error saving custom role:', err);
      setError(err.message);
      // TODO: Add error notification
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!window.confirm('Are you sure you want to delete this custom role? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/teams/${teamId}/custom-roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete custom role: ${response.status}`);
      }

      console.log('Custom role deleted successfully!');
      fetchRolesAndPermissions(); // Refetch data
      // TODO: Add success notification
    } catch (err: any) {
      console.error('Error deleting custom role:', err);
      setError(err.message);
      // TODO: Add error notification
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Custom Roles
        </Typography>
        <Button variant="contained" color="primary" onClick={handleOpenCreateModal}>
          Create New Custom Role
        </Button>
      </Box>

      {customRoles.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
          No custom roles defined yet. Create your first custom role!
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="custom roles table">
            <TableHead>
              <TableRow>
                <TableCell>Role Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customRoles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>{role.name}</TableCell>
                  <TableCell>{role.description}</TableCell>
                  <TableCell>{role.permissions.map(p => p.permission.name).join(', ')}</TableCell>
                  <TableCell>
                    <IconButton aria-label="edit role" onClick={() => handleOpenEditModal(role)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton aria-label="delete role" onClick={() => handleDeleteRole(role.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Custom Role Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>{editingRole ? 'Edit Custom Role' : 'Create New Custom Role'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="role-name"
            label="Role Name"
            type="text"
            fullWidth
            variant="outlined"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="role-description"
            label="Description (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={roleDescription}
            onChange={(e) => setRoleDescription(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 1 }}>
            Permissions
          </Typography>
          <FormGroup>
            {permissions.map((permission) => (
              <FormControlLabel
                key={permission.id}
                control={
                  <Checkbox
                    checked={selectedPermissions.includes(permission.name)}
                    onChange={() => handlePermissionChange(permission.name)}
                    name={permission.name}
                  />
                }
                label={`${permission.name} (${permission.description || 'No description'})`}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSaveRole} variant="contained" color="primary">
            {editingRole ? 'Save Changes' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomRoleManagement;
